/**
 * Shows activity tracking dropdown and manages activities.
 * @constructor
 * @param {array} activities - List of activities
 * @param {string} activityCountId - Id to show total Activities count
 * @param {string} activityDropDownId - Id to bind hide and show activity dropdown
 */

/**
 * add this link/css to html file
 * <link rel="stylesheet" type="text/css" href="/WebInterface/new-ui/modules/activity-tracking/activity-tracking.css" />
 */

import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import templates from "./template.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { operationStatus } from "/WebInterface/new-ui/assets/js/constants.js";

export default class ActivityTracking {
  constructor({ activityCountId, activityDropDownId }) {
    this.activities = [];
    this.activityCountId = activityCountId;
    this.activityDropDownId = activityDropDownId;
    this.init();
  }

  // Initializes the activity tracking component
  init = () => {
    this.createActivityModal();
    this.createActivity();
  };

  // Retrieves the status of an activity by its id
  getStatusById = (id) => {
    const activity = this.activities.find((item) => item.id === id);
    return activity ? activity.status : null;
  };

  // Creates or updates an activity
  createOrUpdate = (activity = {}) => {
    if (activity.id) {
      const curActivity = this.activities.find(
        (item) => item.id === activity.id
      );
      if (activity.show) this.show();
      if (curActivity) {
        this.update(activity);
        return curActivity.id;
      } else {
        return this.add(activity);
      }
    } else {
      return this.add(activity);
    }
  };

  // Adds a new activity
  add = (activity = {}) => {
    const id = activity.id || utils.getUId();
    const newActivity = {
      hasProgressBar: true,
      cancelable: true,
      ...activity,
      id,
    };
    this.activities.push(newActivity);
    this.createActivity();
    return id;
  };

  // Updates an existing activity
  update = (updatedActivity = {}) => {
    const indexOfActivityToBeUpdated = this.activities.findIndex(
      (item) => item.id === updatedActivity.id
    );

    if (indexOfActivityToBeUpdated !== -1) {
      this.activities[indexOfActivityToBeUpdated] = {
        ...this.activities[indexOfActivityToBeUpdated],
        ...updatedActivity,
      };
    }

    this.createActivity();
  };

  // Removes an activity by its id
  remove = (id) => {
    let hasErrorActivities = false;

    this.activities = this.activities.filter((item) => {
      if (item.status === operationStatus.FAILED && item.id !== id) {
        hasErrorActivities = true;
      }
      return item.id !== id;
    });

    const activityCountEl = _$(`#${this.activityCountId}`);
    if (hasErrorActivities) {
      activityCountEl.addClass("error");
    } else {
      activityCountEl.removeClass("error");
    }

    this.createActivity();
  };

  // Creates the activity modal container
  createActivityModal = () => {
    this.activityModal = utils.createElement("div", ["activity-section"]);
    document.body.append(this.activityModal);
    const activityList = this.createActivityList();
    this.activityModal.append(activityList);
    return this.activityModal;
  };

  // Creates the activity list container
  createActivityList = () => {
    this.activityList = utils.createElement("div", ["activity-list"]);
    return this.activityList;
  };

  // Renders the activity elements
  createActivity = () => {
    if (this.activities.length > 0) {
      const scrollTopBeforeRendering = this.activityList.scrollTop;
      this.activityList.innerHTML = "";

      this.activities.forEach((element) => {
        const {
          type,
          message,
          cancelable,
          hasProgressBar,
          currentProgress,
          onCancel,
          onClick,
          maxProgress = 100,
          hasInfiniteLoader = true,
          status,
        } = element;

        let manipulatedCurrentProgress = currentProgress;
        let manipulatedCurrentProgressPercentage = parseInt(
          (currentProgress / maxProgress) * 100
        );

        if (currentProgress > maxProgress) {
          manipulatedCurrentProgress = maxProgress;
        }

        const { COMPLETED, FAILED, CANCELED } = operationStatus;
        const noProgressStatuses = [COMPLETED, FAILED, CANCELED];

        const completed = noProgressStatuses.find(
          (noProgressStatus) => status === noProgressStatus
        );

        let activity = utils.createElement(
          "div",
          ["activity", status || null],
          [],
          templates.activityTemplate({
            type,
            message,
            maxProgress,
            currentProgress: manipulatedCurrentProgress,
            currentProgressPercentage: manipulatedCurrentProgress
              ? manipulatedCurrentProgressPercentage + "%"
              : "",
          })
        );

        if (status === FAILED) {
          const activityCountEl = _$(`#${this.activityCountId}`);
          activityCountEl.addClass("error");
        }

        // Click Activity
        if (typeof onClick === "function") {
          activity.addEventListener("click", (e) => {
            e.stopPropagation();
            onClick(element);
          });
        }

        // Cancel Button
        if (cancelable) {
          const cancelButton = activity.querySelector(".cancel-button");
          if (cancelButton) {
            cancelButton.classList.remove("is-hidden");
            cancelButton.addEventListener("click", (e) => {
              e.stopPropagation();
              onCancel(element);
            });
          }
        }

        // Progress Bar
        if (hasProgressBar && !completed) {
          const progressBar = activity.querySelector(".progress-bar");
          progressBar.classList.remove("is-hidden");
        }

        // Infinite Loader
        if (hasInfiniteLoader) {
          const progressBar = activity.querySelector(".progress");
          const completedPercentage = activity.querySelector(
            ".completed-percentage"
          );
          completedPercentage.classList.add("is-hidden");
          progressBar.removeAttribute("value");
        }

        this.activityList.scrollTop = scrollTopBeforeRendering;
        this.activityList.append(activity);
      });

      if (this.activityCountId && _$(`#${this.activityCountId}`)) {
        const activityCountEl = _$(`#${this.activityCountId}`).el();
        activityCountEl.classList.remove("is-hidden");
        activityCountEl.innerText = this.activities.length;
      }
    } else {
      if (this.activityCountId && _$(`#${this.activityCountId}`)) {
        const activityCountEl = _$(`#${this.activityCountId}`).el();
        activityCountEl.classList.add("is-hidden");

        this.activityList.innerHTML = templates.emptyTemplate({
          i18nKey: "nothingToDisplay",
          nothingToDisplayText: t("nothingToDisplay"),
        });
      }
    }
  };

  // Triggers an action on an activity
  trigger = (type, id) => {
    const activity = this.activities.find((item) => item.id === id);

    if (activity) {
      if (typeof activity.onClick === "function" && type === "click") {
        activity.onClick(activity);
      }
      if (typeof activity.onCancel === "function" && type === "cancel") {
        activity.onCancel(activity);
      }
    }
  };

  // Shows the activity tracking dropdown
  show = () => {
    if (
      this.activityDropDownId &&
      !_$(`#${this.activityDropDownId}`).hasClass("is-active")
    ) {
      _$(`#${this.activityDropDownId}`).find(`.dropdown-trigger`).el().click();
    }

    clearTimeout(this.hideTimeout);
    this.hideTimeout = setTimeout(() => {
      this.hide();
    }, 3000);
  };

  // Hides the activity tracking dropdown
  hide = () => {
    if (
      this.activityDropDownId &&
      _$(`#${this.activityDropDownId}`).hasClass("is-active")
    ) {
      _$(`#${this.activityDropDownId}`).find(`.dropdown-trigger`).el().click();
    }
  };
}
